import { useCallback, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/Init';
import { COPY } from '../contracts/copyMap';
import { checkoutReturnUrls } from '../lib/billingReturnUrls';
import { track } from '../analytics/track';
import { CONVERSION_EVENTS } from '../analytics/conversion';

// The one way into Stripe checkout from the app shell.
//
// Extracted when the routine paywall grew a second "start a trial" button
// alongside UnlockGateModal's. The interesting part is not the callable — it is
// the three-way response handling, which is easy to get subtly wrong twice:
//
//   success + checkout_url -> full off-origin navigation. `submitting` is left
//                             SET on purpose; the page is going away, and
//                             clearing it would flash an enabled button first.
//   ALREADY_SUBSCRIBED     -> reload, not an error. It means the client's
//                             entitlement snapshot is behind the server's, and
//                             a reload resyncs it. An error message here would
//                             be one the user cannot act on.
//   anything else          -> surface the server's message if it sent one.
//
// `label` tags the console.error AND the trial_cta_clicked event's `surface`,
// so both a failure and a funnel row say which surface they came from rather
// than pointing every report at this file.
export default function useStartTrial(label = 'useStartTrial') {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const startTrial = useCallback(async () => {
    // Every trial button in the product routes through this hook, so this one
    // call is the whole "clicked BEGIN FREE TRIAL" side of the funnel.
    // track() never throws, so it cannot block the checkout.
    track(CONVERSION_EVENTS.TRIAL_CTA_CLICKED, { surface: label });
    setSubmitting(true);
    setError(null);
    try {
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
      // The CURRENT origin, not the server's hardcoded fallback. See
      // billingReturnUrls — without this, checkout returned everyone to
      // https://mogged.app and Firebase Auth (per-origin) treated them as
      // signed out.
      const res = await createCheckoutSession(checkoutReturnUrls());
      const data = res?.data;
      if (data?.status === 'success' && data.checkout_url) {
        window.location.assign(data.checkout_url);
        return;
      }
      if (data?.error_code === 'ALREADY_SUBSCRIBED') {
        window.location.reload();
        return;
      }

      setError(data?.error_message || COPY.MEMBERSHIP_ERROR);
      setSubmitting(false);
    } catch (err) {
      console.error(`${label}: checkout failed`, err);
      setError(COPY.MEMBERSHIP_ERROR);
      setSubmitting(false);
    }
  }, [label]);

  return { startTrial, submitting, error };
}
